<template>
  <div
    v-if="locations.length"
    class="cn-location-switcher relative"
    :class="{
      'cn-location-switcher--dark': dark,
    }"
  >
    <button
      class="cn-location-switcher__button flex items-center"
      aria-label="search"
      @click="toggle"
    >
      <span class="cn-location-switcher__button-text">
        <span class="text-sm">
          {{ $t('region') }} {{ currentLocationName }}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg"
          class="inline"
          :class="{ 'rotate-180': isOpen }"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M7.707 9L4.06 5.354L3.707 5L3 5.707L3.353 6.061L7.176 9.885L7.706 10.415L8.059 10.062L12.059 6.062L12.414 5.708L11.707 5L11.354 5.354L7.706 9H7.707Z"
          />
        </svg>
      </span>
    </button>
    <transition
      enter-active-class="transform duration-300"
      enter-from-class="opacity-0 translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transform duration-300"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-4"
    >
      <div
        v-if="isOpen"
        class="cn-location-switcher__dropdown fixed bottom-0 left-0 right-0 top-0 flex w-screen items-end backdrop-blur-sm sm:absolute sm:bottom-auto sm:left-auto sm:top-full sm:mt-2 sm:w-56"
        :class="{ 'xl:w-[28rem]': locations.length > 14 }"
      >
        <div
          ref="target"
          class="cn-location-switcher__dropdown-block max-h-88 w-full overflow-y-auto py-2 shadow-lg"
          :class="{ 'xl:grid xl:grid-cols-2': locations.length > 14 }"
        >
          <a
            v-for="(location, i) in locations"
            :key="i"
            :href="location.url"
            class="cn-location-switcher__location flex w-full items-center px-6 py-3"
          >
            <span class="text-sm">
              {{ location.name }}
            </span>
            <svg
              v-if="normalizeDomain(location.url) === currentDomain"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="ml-auto"
            >
              <path
                d="M9.99997 15.586L6.70697 12.293L5.29297 13.707L9.99997 18.414L19.707 8.70697L18.293 7.29297L9.99997 15.586Z"
                fill="currentColor"
              />
            </svg>
          </a>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import {
  onClickOutside,
  useBreakpoints,
  breakpointsTailwind,
} from '@vueuse/core';
import { useBodyScrollLock, useMenus } from '@/composables';

interface Props {
  dark?: boolean;
}

defineProps<Props>();
const breakpoints = useBreakpoints(breakpointsTailwind);
const isDesktop = breakpoints.greater('xl');

const { menuLocations: locations } = useMenus();
const currentDomain = ref('');
const currentLocationName = computed(() => {
  const location = locations.value?.find(
    (item) => normalizeDomain(item.url) === currentDomain.value,
  );
  return location && location.name ? location.name : '';
});

const isOpen = ref(false);
useBodyScrollLock(() => isOpen.value && !isDesktop.value);

const close = () => {
  isOpen.value = false;
};
const toggle = () => {
  isOpen.value = !isOpen.value;
};

const target = ref(null);

const normalizeDomain = (url: string) => {
  return url?.replace(/\/+$/, '');
};

onClickOutside(target, () => {
  close();
});

onMounted(() => {
  currentDomain.value = `${window.location.origin}`;
});

defineExpose({
  close,
  toggle,
});
</script>

<style lang="scss">
.cn-location-switcher {
  --ab-canyon-location-switcher-button-text-color-text: var(
    --ab-canyon-color-dark
  );
  --ab-canyon-location-switcher-dropdown-color-bg: var(--ab-canyon-color-dark);
  --ab-canyon-location-switcher-dropdown-block-color-bg: var(
    --ab-canyon-color-light
  );
  --ab-canyon-location-switcher-location-color-bg-hover: var(
    --ab-canyon-color-neutral-light
  );
  --ab-canyon-location-switcher-location-icon-color-text: var(
    --ab-canyon-color-accent
  );

  --ab-canyon-location-switcher-button-text-color-text-dark: var(
    --ab-canyon-color-light
  );

  &__button-text {
    color: var(--ab-canyon-location-switcher-button-text-color-text);
  }
  &__dropdown {
    background-color: color-mix(
      in srgb,
      var(--ab-canyon-location-switcher-dropdown-color-bg),
      transparent 50%
    );
    z-index: 11;
  }
  &__dropdown-block {
    background-color: var(
      --ab-canyon-location-switcher-dropdown-block-color-bg
    );
  }
  &__location {
    &:hover {
      background-color: var(
        --ab-canyon-location-switcher-location-color-bg-hover
      );
    }

    svg {
      color: var(--ab-canyon-location-switcher-location-icon-color-text);
    }
  }
  &--dark {
    .cn-location-switcher {
      &__button-text {
        color: var(--ab-canyon-location-switcher-button-text-color-text-dark);
      }
    }
  }
}
</style>
